import { motion } from "framer-motion";
import { AlertTriangle, ArrowRight, Loader2, Phone, RefreshCw, Sparkles, Trash2, UserPlus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import ConfirmModal from "../components/common/ConfirmModal";
import { api } from "../lib/api";


export default function TestCandidate() {
    const [loading, setLoading] = useState(true);
    const [candidate, setCandidate] = useState(null);
    const [phone, setPhone] = useState("");
    const [firstName, setFirstName] = useState("Marie");
    const [lastName, setLastName] = useState("TESTÉE");
    const [civilite, setCivilite] = useState("Mme");
    const [city, setCity] = useState("Paris");
    const [creating, setCreating] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/test-candidate");
            setCandidate(data);
            if (data?.phone) setPhone(data.phone);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function handleCreate() {
        if (!phone.trim()) { toast.error("Numéro de téléphone requis"); return; }
        setCreating(true);
        try {
            const { data } = await api.post("/admin/test-candidate", {
                phone: phone.trim(),
                first_name: firstName.trim() || "Marie",
                last_name: lastName.trim() || "TESTÉE",
                civilite: civilite || "Mme",
                city: city.trim() || "Paris",
            });
            setCandidate(data);
            toast.success(candidate ? "Candidat de test régénéré" : "Candidat de test créé");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setCreating(false);
        }
    }

    async function handleDelete() {
        try {
            await api.delete("/admin/test-candidate");
            toast.success("Candidat de test supprimé");
            setCandidate(null);
            setConfirmDelete(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    }

    const [cleanupPreview, setCleanupPreview] = useState({ test_candidates: 0, site_webhook_tests: 0 });
    const [cleaningKind, setCleaningKind] = useState(null);
    const [confirmCleanup, setConfirmCleanup] = useState(null); // "test" | "webhook" | null

    const loadCleanup = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/cleanup/preview");
            setCleanupPreview(data);
        } catch (_e) { /* ignore */ }
    }, []);

    useEffect(() => { loadCleanup(); }, [loadCleanup, candidate]);

    async function runCleanup(kind) {
        setCleaningKind(kind);
        const endpoint = kind === "test"
            ? "/admin/cleanup/test-candidates"
            : "/admin/cleanup/site-webhook-tests";
        try {
            const { data } = await api.post(endpoint);
            const summary = data.summary || {};
            toast.success(
                `${summary.candidates || 0} candidat${(summary.candidates || 0) > 1 ? "s" : ""} supprimé${(summary.candidates || 0) > 1 ? "s" : ""}`,
                {
                    description: `Scorecards : ${summary.scorecards || 0} · Messages : ${summary.outbound_messages || 0} · Notifs : ${summary.notifications || 0}`,
                    duration: 6000,
                },
            );
            setConfirmCleanup(null);
            await Promise.all([load(), loadCleanup()]);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Nettoyage impossible");
        } finally {
            setCleaningKind(null);
        }
    }

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-shariti-orange" />
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto space-y-6" data-testid="test-candidate-page">
            <header>
                <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                    <Sparkles className="h-3 w-3" /> Outils QA · Admin
                </div>
                <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-tight text-shariti-black">
                    Candidat de test
                </h1>
                <p className="mt-2 text-sm font-medium text-slate-500">
                    Créez un candidat fictif avec <strong>votre propre numéro de téléphone</strong> pour tester en toute sécurité l'envoi de SMS, WhatsApp, et toutes les actions du tunnel (« Pas de réponse », « Récap entretien », immersion programmée…).
                </p>
            </header>

            {candidate && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-3xl bg-gradient-to-br from-shariti-orange-soft to-shariti-cream border-2 border-shariti-orange/20 p-6"
                    data-testid="test-candidate-active"
                >
                    <div className="flex items-start gap-4 flex-wrap">
                        <div className="h-14 w-14 rounded-2xl bg-shariti-orange text-white flex items-center justify-center text-xl font-black shrink-0">
                            🧪
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h2 className="text-xl font-extrabold text-shariti-black">
                                    {candidate.civilite} {candidate.first_name} {candidate.last_name}
                                </h2>
                                <span className="text-[10px] font-bold uppercase tracking-widest bg-shariti-orange text-white px-2 py-0.5 rounded-full">
                                    TEST
                                </span>
                            </div>
                            <div className="mt-2 grid sm:grid-cols-2 gap-2 text-sm font-medium text-slate-700">
                                <div className="flex items-center gap-1.5">
                                    <Phone className="h-3.5 w-3.5 text-shariti-orange" /> {candidate.phone}
                                </div>
                                <div>📧 {candidate.email}</div>
                                <div>📍 {candidate.city}</div>
                                <div>🎯 Score IA : {candidate.ai_score}/100</div>
                            </div>
                            <Link
                                to={`/candidats/${candidate.id}`}
                                data-testid="test-candidate-open"
                                className="mt-4 inline-flex items-center gap-2 bg-shariti-orange hover:bg-[#d66528] text-white rounded-full font-extrabold px-5 h-10 text-xs transition shadow-btn"
                            >
                                Ouvrir la fiche du candidat de test
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </div>
                </motion.div>
            )}

            <motion.section
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="card-soft p-6 md:p-8 space-y-5"
                data-testid="test-candidate-form"
            >
                <h2 className="text-lg font-extrabold text-shariti-black flex items-center gap-2">
                    <UserPlus className="h-4 w-4 text-shariti-orange" />
                    {candidate ? "Régénérer le candidat de test" : "Créer le candidat de test"}
                </h2>
                <p className="text-xs font-medium text-slate-500 -mt-3">
                    {candidate
                        ? "Régénérer remplace le candidat actuel et efface tout l'historique de communication."
                        : "Un seul candidat de test par espace. Toutes les actions sont safe."}
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">
                            Numéro de téléphone *
                        </span>
                        <input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="06 12 34 56 78"
                            data-testid="test-candidate-phone"
                            className="input-shariti"
                        />
                        <p className="mt-1 text-[10px] font-medium text-slate-400">
                            Idéalement votre propre numéro. Format FR (0612…) ou international (+33...).
                        </p>
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">
                            Civilité
                        </span>
                        <select
                            value={civilite}
                            onChange={(e) => setCivilite(e.target.value)}
                            data-testid="test-candidate-civilite"
                            className="input-shariti"
                        >
                            <option value="Mme">Mme</option>
                            <option value="Mr">Mr</option>
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">
                            Prénom
                        </span>
                        <input
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            data-testid="test-candidate-firstname"
                            className="input-shariti"
                        />
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">
                            Nom
                        </span>
                        <input
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            data-testid="test-candidate-lastname"
                            className="input-shariti"
                        />
                    </label>
                    <label className="block md:col-span-2">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">
                            Ville
                        </span>
                        <input
                            type="text"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            data-testid="test-candidate-city"
                            className="input-shariti"
                        />
                    </label>
                </div>

                <div className="flex items-center justify-between gap-3 flex-wrap pt-2 border-t border-shariti-gray">
                    {candidate && (
                        <button
                            type="button"
                            onClick={() => setConfirmDelete(true)}
                            data-testid="test-candidate-delete"
                            className="inline-flex items-center gap-2 bg-white border border-red-200 text-shariti-red hover:bg-red-50 rounded-full font-bold px-4 h-10 text-xs transition"
                        >
                            <Trash2 className="h-3.5 w-3.5" /> Supprimer le candidat de test
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={handleCreate}
                        disabled={creating || !phone.trim()}
                        data-testid="test-candidate-submit"
                        className="btn-shariti disabled:opacity-50 ml-auto"
                    >
                        {creating
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : (candidate ? <RefreshCw className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />)}
                        {candidate ? "Régénérer" : "Créer le candidat de test"}
                    </button>
                </div>
            </motion.section>

            <motion.section
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="rounded-3xl border-2 border-slate-200 bg-white p-5 space-y-4"
                data-testid="test-candidate-cleanup"
            >
                <div className="flex items-center gap-2">
                    <Trash2 className="h-4 w-4 text-slate-500" />
                    <h2 className="text-sm font-extrabold uppercase tracking-widest text-slate-600">
                        Nettoyage en masse
                    </h2>
                </div>
                <p className="text-xs font-medium text-slate-500">
                    Pour faire du ménage dans les candidats de validation. Action <strong>irréversible</strong> — supprime également les scorecards, messages et notifications associés.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                    <button
                        onClick={() => setConfirmCleanup("test")}
                        disabled={cleanupPreview.test_candidates === 0 || cleaningKind === "test"}
                        data-testid="cleanup-test-candidates"
                        className="group text-left rounded-2xl border-2 border-slate-200 hover:border-shariti-red hover:bg-red-50/40 p-4 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                        <div className="flex items-center justify-between">
                            <div className="text-sm font-extrabold text-shariti-black">🧪 Candidats test</div>
                            <span className="text-xs font-extrabold text-shariti-red bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                                {cleanupPreview.test_candidates}
                            </span>
                        </div>
                        <p className="mt-1.5 text-[11px] font-medium text-slate-500 leading-snug">
                            Les candidats créés depuis cette page (marqués <code>is_test=true</code>).
                        </p>
                    </button>
                    <button
                        onClick={() => setConfirmCleanup("webhook")}
                        disabled={cleanupPreview.site_webhook_tests === 0 || cleaningKind === "webhook"}
                        data-testid="cleanup-site-webhook-tests"
                        className="group text-left rounded-2xl border-2 border-slate-200 hover:border-shariti-red hover:bg-red-50/40 p-4 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                        <div className="flex items-center justify-between">
                            <div className="text-sm font-extrabold text-shariti-black">🌐 Tests Site Webhook</div>
                            <span className="text-xs font-extrabold text-shariti-red bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                                {cleanupPreview.site_webhook_tests}
                            </span>
                        </div>
                        <p className="mt-1.5 text-[11px] font-medium text-slate-500 leading-snug">
                            Les candidatures fictives générées via le bouton « Envoyer un test » de la page Site Shariti.
                        </p>
                    </button>
                </div>
            </motion.section>

            <motion.section
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="rounded-3xl bg-amber-50 border-2 border-amber-200 p-5 flex gap-3"
                data-testid="test-candidate-notice"
            >
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-xs font-medium text-amber-900 leading-relaxed">
                    <strong className="block mb-1">Important :</strong>
                    Ce candidat est marqué <strong>TEST</strong> et reste visible par tous les rôles (admin, pré-recruteur, référent) peu importe leur périmètre d'offres. Il n'a pas d'offre Layan associée, donc il n'apparaîtra dans aucun pipeline de production. Supprimez-le quand vous avez fini vos tests.
                </div>
            </motion.section>

            {confirmDelete && (
                <ConfirmModal
                    title="Supprimer le candidat de test ?"
                    body={<>Le candidat <strong>{candidate?.first_name} {candidate?.last_name}</strong> et tout l'historique de communications associé seront supprimés.</>}
                    confirmLabel="Supprimer"
                    tone="red"
                    onCancel={() => setConfirmDelete(false)}
                    onConfirm={handleDelete}
                />
            )}

            {confirmCleanup && (
                <ConfirmModal
                    testid="confirm-bulk-cleanup"
                    title={confirmCleanup === "test" ? "Nettoyer tous les candidats test ?" : "Nettoyer toutes les candidatures Site Webhook test ?"}
                    body={
                        <>
                            <p>
                                Vous êtes sur le point de supprimer <strong>{confirmCleanup === "test" ? cleanupPreview.test_candidates : cleanupPreview.site_webhook_tests}</strong> candidat{(confirmCleanup === "test" ? cleanupPreview.test_candidates : cleanupPreview.site_webhook_tests) > 1 ? "s" : ""}.
                            </p>
                            <div className="mt-3 rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-xs text-shariti-red">
                                <strong>⚠️ Cette action est irréversible.</strong> Elle supprimera également :
                                <ul className="mt-1.5 ml-4 list-disc space-y-0.5">
                                    <li>toutes leurs scorecards,</li>
                                    <li>tous leurs messages SMS / WhatsApp,</li>
                                    <li>toutes leurs notifications associées.</li>
                                </ul>
                            </div>
                        </>
                    }
                    confirmLabel="Supprimer définitivement"
                    cancelLabel="Annuler"
                    tone="red"
                    onCancel={() => setConfirmCleanup(null)}
                    onConfirm={() => runCleanup(confirmCleanup)}
                />
            )}
        </div>
    );
}
